import { gql, useApolloClient } from "@apollo/client";
import {
  CREATE_DATA_CONNECTION_HEX_VERSION_LINK,
  ClientAnalyticsEventType,
  DataBrowserTab,
  DataBrowserTableSelectedEventData,
  DataConnectionHexVersionLinkId,
  DataConnectionId,
  DataSourceTableId,
  HexId,
  SemanticDatasetId,
  SemanticDatasetName,
  SemanticProjectId,
  SemanticProjectVersionString,
  SemanticViewId,
  SemanticViewName,
  guardNever,
  hexIdToHumanHexIdWithoutTitle,
  uuid,
} from "@hex/common";
import { useCallback } from "react";
import { useHistory } from "react-router";

import { useToaster } from "../components/common/Toasts.js";
import { useCreateNoCodeCell } from "../components/data/schema-browser-v2/useCreateNoCodeCell.js";
import {
  importedDataConnectionIdsSetSelector,
  useDataConnectionHexVersionLinksGetter,
} from "../hex-version-multiplayer/state-hooks/dataConnectionHexVersionLinkStateHooks.js";
import { useDataBrowserGetter } from "../hooks/data/dataBrowserStateHooks.js";
import { Routes } from "../route/routes.js";
import { useHexVersionAOContext } from "../util/hexVersionAOContext.js";
import { trackEvent } from "../util/trackEvent.js";

import {
  CreateExploreFromSemanticDatasetDocument,
  CreateExploreFromSemanticDatasetMutation,
  CreateExploreFromSemanticDatasetMutationVariables,
  CreateExploreFromSemanticViewDocument,
  CreateExploreFromSemanticViewMutation,
  CreateExploreFromSemanticViewMutationVariables,
  useCreateExploreFromWarehouseTableMutation,
} from "./useCreateExplore.generated.js";

gql`
  mutation CreateExploreFromWarehouseTable(
    $dataSourceTableId: DataSourceTableId!
  ) {
    createExploreFromData(dataSourceTableId: $dataSourceTableId) {
      id
    }
  }
`;

gql`
  mutation CreateExploreFromSemanticDataset(
    $semanticDatasetId: SemanticDatasetId!
    $semanticProjectVersionString: SemanticProjectVersionString
    $initialMeasureName: String
    $initialDimensionName: String
  ) {
    createExploreFromData(
      semanticDatasetId: $semanticDatasetId
      semanticProjectVersionString: $semanticProjectVersionString
      initialMeasureName: $initialMeasureName
      initialDimensionName: $initialDimensionName
    ) {
      id
    }
  }
`;

gql`
  mutation CreateExploreFromSemanticView(
    $semanticViewId: SemanticViewId!
    $semanticProjectVersionString: SemanticProjectVersionString
    $initialMeasureName: String
    $initialDimensionName: String
  ) {
    createExploreFromData(
      semanticViewId: $semanticViewId
      semanticProjectVersionString: $semanticProjectVersionString
      initialMeasureName: $initialMeasureName
      initialDimensionName: $initialDimensionName
    ) {
      id
    }
  }
`;

export function useCreateExploreFromWarehouseTable(
  dataSourceTableId?: DataSourceTableId | null,
  trackingInfo?: {
    browserType: DataBrowserTableSelectedEventData["browserType"];
    dataBrowserTab: DataBrowserTab;
    dataConnectionId?: DataConnectionId;
  },
): (dataSourceTableIdOverride?: DataSourceTableId) => Promise<void> {
  const toaster = useToaster();
  const history = useHistory();

  const [createExplore] = useCreateExploreFromWarehouseTableMutation();

  return useCallback(
    async (dataSourceTableIdOverride?: DataSourceTableId) => {
      const tableId = dataSourceTableIdOverride ?? dataSourceTableId;
      if (tableId == null) {
        return;
      }

      try {
        const { data } = await createExplore({
          variables: { dataSourceTableId: tableId },
        });

        if (data?.createExploreFromData.id == null) {
          toaster.show({
            message: `Failed to create explore. Did not receive an ID from server`,
            intent: "danger",
          });
          return;
        }
        if (trackingInfo) {
          trackEvent(ClientAnalyticsEventType.DATA_BROWSER_TABLE_SELECTED, {
            browserType: trackingInfo.browserType,
            dataSourceTableId: tableId ?? null,
            dataConnectionId: trackingInfo.dataConnectionId ?? null,
            dataBrowserTab: trackingInfo.dataBrowserTab,
            tableActionTaken: "EXPLORE_UI",
          });
        }
        Routes.EXPLORE.push(history, {
          hexLocator: hexIdToHumanHexIdWithoutTitle(
            data.createExploreFromData.id,
          ),
        });
      } catch (e) {
        console.error(e);
        toaster.show({
          message: `Failed to create explore. Error: ${e}`,
          intent: "danger",
        });
      }
    },
    [createExplore, dataSourceTableId, history, toaster, trackingInfo],
  );
}

export function useCreateExploreFromSemanticDatasetCallback(): (
  semanticDatasetId: SemanticDatasetId | null,
  options?: {
    preventNavigation?: boolean;
    semanticProjectVersionString?: SemanticProjectVersionString;
    initialMeasureName?: string;
    initialDimensionName?: string;
    trackingInfo?: {
      browserType: DataBrowserTableSelectedEventData["browserType"];
      semanticProjectId: SemanticProjectId;
      dataBrowserTab: DataBrowserTab;
    };
  },
) => Promise<HexId | null> {
  const toaster = useToaster();
  const history = useHistory();

  const apolloClient = useApolloClient();

  return useCallback(
    async (semanticDatasetId, options = {}) => {
      if (semanticDatasetId == null) {
        return null;
      }

      try {
        const { data } = await apolloClient.mutate<
          CreateExploreFromSemanticDatasetMutation,
          CreateExploreFromSemanticDatasetMutationVariables
        >({
          mutation: CreateExploreFromSemanticDatasetDocument,
          variables: {
            semanticDatasetId,
            initialMeasureName: options.initialMeasureName ?? null,
            initialDimensionName: options.initialDimensionName ?? null,
            semanticProjectVersionString:
              options.semanticProjectVersionString ?? null,
          },
        });

        const hexId = data?.createExploreFromData.id;

        if (hexId == null) {
          toaster.show({
            message: `Failed to create explore. Did not receive an ID from server`,
            intent: "danger",
          });
          return null;
        }

        const { trackingInfo } = options;
        if (trackingInfo) {
          trackEvent(ClientAnalyticsEventType.SEMANTIC_DATASET_SELECTED, {
            browserType: trackingInfo.browserType,
            tableActionTaken: "EXPLORE_UI",
            semanticProjectId: trackingInfo.semanticProjectId,
            semanticDatasetId: semanticDatasetId,
            dataBrowserTab: trackingInfo.dataBrowserTab,
          });
        }

        if (!options.preventNavigation) {
          Routes.EXPLORE.push(history, {
            hexLocator: hexIdToHumanHexIdWithoutTitle(hexId),
          });
        }
        return hexId;
      } catch (e) {
        console.error(e);
        toaster.show({
          message: `Failed to create explore. Error: ${e}`,
          intent: "danger",
        });
        return null;
      }
    },
    [apolloClient, history, toaster],
  );
}

export function useCreateExploreFromSemanticViewCallback(): (
  semanticViewId: SemanticViewId | null,
  options?: {
    preventNavigation?: boolean;
    semanticProjectVersionString?: SemanticProjectVersionString;
    initialMeasureName?: string;
    initialDimensionName?: string;
    trackingInfo?: {
      browserType: DataBrowserTableSelectedEventData["browserType"];
      semanticProjectId: SemanticProjectId;
      dataBrowserTab: DataBrowserTab;
    };
  },
) => Promise<HexId | null> {
  const toaster = useToaster();
  const history = useHistory();

  const apolloClient = useApolloClient();

  return useCallback(
    async (semanticViewId, options = {}) => {
      if (semanticViewId == null) {
        return null;
      }

      try {
        const { data } = await apolloClient.mutate<
          CreateExploreFromSemanticViewMutation,
          CreateExploreFromSemanticViewMutationVariables
        >({
          mutation: CreateExploreFromSemanticViewDocument,
          variables: {
            semanticViewId,
            initialMeasureName: options.initialMeasureName ?? null,
            initialDimensionName: options.initialDimensionName ?? null,
            semanticProjectVersionString:
              options.semanticProjectVersionString ?? null,
          },
        });
        const hexId = data?.createExploreFromData.id;

        if (hexId == null) {
          toaster.show({
            message: `Failed to create explore. Did not receive an ID from server`,
            intent: "danger",
          });
          return null;
        }

        const { trackingInfo } = options;
        if (trackingInfo) {
          trackEvent(ClientAnalyticsEventType.SEMANTIC_VIEW_SELECTED, {
            browserType: trackingInfo.browserType,
            tableActionTaken: "EXPLORE_UI",
            semanticProjectId: trackingInfo.semanticProjectId,
            semanticViewId: semanticViewId,
            dataBrowserTab: trackingInfo.dataBrowserTab,
          });
        }

        if (!options.preventNavigation) {
          Routes.EXPLORE.push(history, {
            hexLocator: hexIdToHumanHexIdWithoutTitle(hexId),
          });
        }
        return hexId;
      } catch (e) {
        console.error(e);
        toaster.show({
          message: `Failed to create explore. Error: ${e}`,
          intent: "danger",
        });
        return null;
      }
    },
    [apolloClient, history, toaster],
  );
}

export function useCreateExploreFromSemanticResourceCallback(): (
  resource: { viewId: SemanticViewId } | { datasetId: SemanticDatasetId },
  options?: {
    preventNavigation?: boolean;
    semanticProjectVersionString?: SemanticProjectVersionString;
    initialMeasureName?: string;
    initialDimensionName?: string;
    trackingInfo?: {
      browserType: DataBrowserTableSelectedEventData["browserType"];
      semanticProjectId: SemanticProjectId;
      dataBrowserTab: DataBrowserTab;
    };
  },
) => Promise<HexId | null> {
  const createExploreFromDataset =
    useCreateExploreFromSemanticDatasetCallback();
  const createExploreFromView = useCreateExploreFromSemanticViewCallback();
  return useCallback(
    async (resource, options = {}) => {
      if ("viewId" in resource) {
        return createExploreFromView(resource.viewId, options);
      } else if ("datasetId" in resource) {
        return createExploreFromDataset(resource.datasetId, options);
      } else {
        guardNever(resource, "resource");
        return null;
      }
    },
    [createExploreFromDataset, createExploreFromView],
  );
}

export type CreateExploreCellInProject = (
  params:
    | {
        dataConnectionId: DataConnectionId;
        semanticDatasetName: SemanticDatasetName;
        semanticProjectId: SemanticProjectId;
      }
    | {
        dataConnectionId: DataConnectionId;
        semanticViewName: SemanticViewName;
        semanticProjectId: SemanticProjectId;
      },
) => Promise<void>;

/**
 * A hook to get a callback to create an ExploreCell in a Project.
 *
 * (Only works in a project context and uses lazy getters)
 */
export function useCreateExploreCellInProject(): CreateExploreCellInProject {
  const getInsertLocation = useDataBrowserGetter({
    selector: (state) => state.insertLocation,
  });

  const { createExploreCell } = useCreateNoCodeCell({
    insertLocation: getInsertLocation,
  });

  const importConnectionIfNeeded = useImportConnectionIfNeeded();

  const createExploreCellInProject = useCallback<CreateExploreCellInProject>(
    async (params) => {
      const { dataConnectionId, semanticProjectId } = params;
      await importConnectionIfNeeded(dataConnectionId);
      if ("semanticDatasetName" in params) {
        createExploreCell({
          semanticDatasetName: params.semanticDatasetName,
          semanticProjectId,
          dataConnectionId,
        });
      } else if ("semanticViewName" in params) {
        createExploreCell({
          semanticViewName: params.semanticViewName,
          semanticProjectId,
          dataConnectionId,
        });
      }
    },
    [importConnectionIfNeeded, createExploreCell],
  );

  return createExploreCellInProject;
}
/**
 * A hook to get a callback to ensure a DataConnection is imported in a Project.
 *
 * (Only works in a project context and uses lazy getters)
 */
export function useImportConnectionIfNeeded(): (
  dataConnectionId: DataConnectionId,
) => Promise<void> {
  const { dispatchAO } = useHexVersionAOContext();

  const getImportedConnections = useDataConnectionHexVersionLinksGetter({
    selector: importedDataConnectionIdsSetSelector,
    safe: true,
  });

  return useCallback(
    async (dataConnectionId: DataConnectionId) => {
      if (
        dataConnectionId == null ||
        getImportedConnections().has(dataConnectionId)
      ) {
        return;
      }

      await dispatchAO(
        CREATE_DATA_CONNECTION_HEX_VERSION_LINK.create({
          dataConnectionHexVersionLinkId:
            uuid() as DataConnectionHexVersionLinkId,
          dataConnectionId,
        }),
      ).promise;
    },
    [dispatchAO, getImportedConnections],
  );
}
